import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useWallet } from '../contexts/WalletContext'
import {
  CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES,
  lotteryService,
  type BetSlip,
  type BetSlipItem,
  type LotteryRound,
} from '../services/lotteryService'

const formatDate = (dateStr: string) => {
  try {
    const d = new Date(dateStr)
    return d.toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return dateStr
  }
}

const itemStatusLabel: Record<string, string> = {
  pending: 'รอผล',
  confirmed: 'รอผล',
  won: 'ถูกรางวัล',
  lost: 'ไม่ถูกรางวัล',
  partial_win: 'ถูกรางวัลบางส่วน',
  cancelled: 'ยกเลิก',
  refunded: 'คืนเงิน',
}

const itemStatusClass: Record<string, string> = {
  pending: 'bgc-13',
  confirmed: 'bgc-13',
  won: 'bgc-11',
  lost: 'bgc-9',
  partial_win: 'bgc-11',
  cancelled: 'bgc-9',
  refunded: 'bgc-9',
}

const normalizeStatus = (status?: string) => String(status || '').trim().toLowerCase()

const getItemStatusLabel = (item: BetSlipItem) => {
  if (item.isWinner === true) return 'ถูกรางวัล'

  const normalizedStatus = normalizeStatus(item.status)
  return itemStatusLabel[normalizedStatus] || (normalizedStatus ? normalizedStatus : 'รอผล')
}

const getItemStatusClass = (item: BetSlipItem) => {
  if (item.isWinner === true) return 'bgc-11'

  const normalizedStatus = normalizeStatus(item.status)
  return itemStatusClass[normalizedStatus] || 'bgc-13'
}

const getCustomerCancelWindowMinutes = (slip: BetSlip | null) => {
  const configured = Number(slip?.customerCancelWindowMinutes)
  return Number.isFinite(configured) && configured > 0
    ? configured
    : CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES
}

const canCustomerCancelSlip = (slip: BetSlip | null, round: LotteryRound | null) => {
  if (!slip) return false

  const status = normalizeStatus(slip.status)
  if (slip.isSettled || ['cancelled', 'refunded'].includes(status)) return false

  const createdAt = slip.createdAt ? new Date(slip.createdAt).getTime() : NaN
  if (!Number.isFinite(createdAt)) return false

  if (round && String(round.status || '').toLowerCase() !== 'open') return false

  return Date.now() - createdAt <= getCustomerCancelWindowMinutes(slip) * 60 * 1000
}

const canCustomerCancelItem = (
  slip: BetSlip | null,
  item: BetSlipItem,
  round: LotteryRound | null
) => {
  if (!canCustomerCancelSlip(slip, round)) return false

  const itemStatus = normalizeStatus(item.status)
  return !['cancelled', 'refunded', 'won', 'lost'].includes(itemStatus) && item.isWinner !== true
}

const getSlipRefundDisplay = (slip: BetSlip, round: LotteryRound | null) => {
  const cancelWindowMinutes = getCustomerCancelWindowMinutes(slip)
  const status = normalizeStatus(slip.status)
  const persistedRefundAmount = Number((slip as BetSlip & { refundAmount?: number }).refundAmount)
  const fallbackRefundAmount = Number(slip.totalEffectiveAmount ?? slip.totalAmount ?? 0)
  const amount = Number.isFinite(persistedRefundAmount) && persistedRefundAmount > 0
    ? persistedRefundAmount
    : fallbackRefundAmount

  const refundedAt = (slip as BetSlip & { refundedAt?: string; updatedAt?: string }).refundedAt
    || (slip as BetSlip & { updatedAt?: string }).updatedAt
    || slip.createdAt

  return {
    status,
    amount: Number.isFinite(amount) ? amount : 0,
    title: status === 'cancelled' || status === 'refunded'
      ? 'ยอดคืนโพย'
      : canCustomerCancelSlip(slip, round)
        ? 'ค่าคืนโพย'
        : 'ยอดสุทธิคืนโพย',
    subtitle: status === 'cancelled' || status === 'refunded'
      ? `คืนโพยเมื่อ ${formatDate(refundedAt)}`
      : canCustomerCancelSlip(slip, round)
        ? `คืนได้ภายใน ${cancelWindowMinutes} นาทีตามยอดสุทธิ`
        : 'ระบบคืนตามยอดสุทธิของโพยเมื่อยังอยู่ในเงื่อนไข',
  }
}

const TicketDetailPage: React.FC = () => {
  const navigate = useNavigate()
  const { id: slipId } = useParams<{ id: string }>()
  const { formatCurrency } = useWallet()
  const [slip, setSlip] = useState<BetSlip | null>(null)
  const [round, setRound] = useState<LotteryRound | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadSlip = useCallback(async () => {
    if (!slipId) {
      setLoading(false)
      setError('ไม่มีรหัสโพย')
      return
    }
    try {
      setLoading(true)
      setError(null)
      const data = await lotteryService.getSlip(slipId)
      setSlip(data)
      if (data?.roundId) {
        const roundData = await lotteryService.getRoundById(String(data.roundId))
        setRound(roundData)
      } else {
        setRound(null)
      }
      if (!data) setError('ไม่พบโพย')
    } catch (e: any) {
      setError(e?.message || 'โหลดโพยไม่สำเร็จ')
      setSlip(null)
      setRound(null)
    } finally {
      setLoading(false)
    }
  }, [slipId])

  useEffect(() => {
    loadSlip()
  }, [loadSlip])

  const handleCancelSlip = useCallback(async () => {
    if (!slip) return
    const cancelWindowMinutes = getCustomerCancelWindowMinutes(slip)

    if (!canCustomerCancelSlip(slip, round)) {
      await Swal.fire({
        icon: 'warning',
        title: 'ไม่สามารถคืนโพยได้',
        text: `คืนโพยได้ภายใน ${cancelWindowMinutes} นาทีหลังจากที่แทง และต้องยังไม่ปิดรับแทง`,
        confirmButtonColor: '#3085d6',
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการคืนโพย',
      html: `คุณต้องการคืนโพย <strong>${slip.slipId ?? slip._id ?? '-'}</strong> ใช่หรือไม่?<br/><small>ระบบจะคืนยอดสุทธิของโพยกลับเข้ากระเป๋า</small>`,
      showCancelButton: true,
      confirmButtonText: 'คืนโพย',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#6c757d',
    })

    if (!result.isConfirmed) return

    const response = await lotteryService.cancelSlip(String(slip.slipId ?? slip._id ?? ''))
    if (!response.success) {
      await Swal.fire({
        icon: 'error',
        title: 'คืนโพยไม่สำเร็จ',
        text: response.message || 'กรุณาลองใหม่อีกครั้ง',
        confirmButtonColor: '#3085d6',
      })
      return
    }

    await Swal.fire({
      icon: 'success',
      title: 'คืนโพยสำเร็จ',
      text: response.message || 'ระบบคืนโพยเรียบร้อยแล้ว',
      confirmButtonColor: '#3085d6',
    })

    await loadSlip()
  }, [loadSlip, round, slip])

  const handleCancelItem = useCallback(async (item: BetSlipItem, itemIndex: number) => {
    if (!slip) return
    const cancelWindowMinutes = getCustomerCancelWindowMinutes(slip)

    if (!canCustomerCancelItem(slip, item, round)) {
      await Swal.fire({
        icon: 'warning',
        title: 'ไม่สามารถคืนเลขได้',
        text: `คืนเลขได้ภายใน ${cancelWindowMinutes} นาทีหลังจากที่แทง และต้องยังไม่ปิดรับแทง`,
        confirmButtonColor: '#3085d6',
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการคืนเลข',
      html: `คุณต้องการคืนเลข <strong>${item.number}</strong> (${item.prizeTypeName || item.prizeTypeCode}) ใช่หรือไม่?<br/><small>ระบบจะคืนยอดสุทธิของรายการนี้กลับเข้ากระเป๋า</small>`,
      showCancelButton: true,
      confirmButtonText: 'คืนเลข',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#6c757d',
    })

    if (!result.isConfirmed) return

    const response = await lotteryService.cancelBetItem(
      String(slip.slipId ?? slip._id ?? ''),
      itemIndex,
      `User cancelled number ${item.number}`
    )
    if (!response.success) {
      await Swal.fire({
        icon: 'error',
        title: 'คืนเลขไม่สำเร็จ',
        text: response.message || 'กรุณาลองใหม่อีกครั้ง',
        confirmButtonColor: '#3085d6',
      })
      return
    }

    await Swal.fire({
      icon: 'success',
      title: 'คืนเลขสำเร็จ',
      text: response.message || 'ระบบคืนเลขเรียบร้อยแล้ว',
      confirmButtonColor: '#3085d6',
    })

    await loadSlip()
  }, [loadSlip, round, slip])

  if (loading) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="text-center py-5">
            <i className="fas fa-spinner fa-spin fs-24 c-white" />
            <p className="c-white mt-2">กำลังโหลดโพย...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !slip) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="w-100 p-2 bgc-4 wrap justify-content-between">
            <a href="#" className="txt-link-d" onClick={(e) => { e.preventDefault(); navigate('/ticket') }}>
              <i className="fas fa-chevron-left me-1" /> กลับ
            </a>
          </div>
          <div className="text-center py-4">
            <h5 className="c-red">{error || 'ไม่พบโพย'}</h5>
            <button type="button" className="bgc-13 c-white px-2 py-1 bdr-5 mt-2" onClick={() => navigate('/ticket')}>
              กลับไปรายการโพย
            </button>
          </div>
        </div>
      </div>
    )
  }

  const winLoss = slip.isSettled && slip.totalWinAmount != null
    ? slip.totalWinAmount - (slip.totalEffectiveAmount || 0)
    : null
  const refundDisplay = getSlipRefundDisplay(slip, round)
  const canCancelSlipNow = canCustomerCancelSlip(slip, round)

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a
                      href="#"
                      className="txt-link-d"
                      onClick={(e) => { e.preventDefault(); navigate('/ticket') }}
                    >
                      <i className="fas fa-chevron-left me-1" /> กลับ
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7 d-grid gap-2">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium">
                      <i className="fas fa-clipboard-list me-2" /> โพย: {slip.slipId}
                    </div>

                    {canCancelSlipNow && (
                      <div className="text-end">
                        <button
                          type="button"
                          className="border-0 bg-warning px-2 py-1 bdr-5"
                          onClick={() => void handleCancelSlip()}
                        >
                          คลิกเพื่อคืนโพย
                        </button>
                      </div>
                    )}

                    <div className="bdr-5 bgc-4 p-2">
                      <div className="row gap-2 mx--1">
                        <div className="col-12 px-1">
                          <div className="bdr-5 bdc-2 bgc-4 p-2">
                            <div className="bgc-6 text-center bdr-5 px-1 bx-sd mb-2">
                              <small>
                                <span className="t-medium"><i className="fas fa-clipboard-list me-1" /> รหัสโพย</span>
                                {' '}{slip.slipId}
                              </small>
                            </div>
                            <div className="d-grid gap-2 align-items-center mt-1" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                              <h6 className="c-red t-medium text-center">{slip.huayName}</h6>
                              <small className="c-dark text-center">รอบที่ {slip.roundNumber}</small>
                            </div>
                            <div className="bgc-13 c-white text-center bdr-5 px-1 bx-sd mt-1 mb-2">
                              <small>
                                <span className="t-medium">ทำรายการเมื่อ: <i className="fas fa-calendar-alt" /></span>
                                {' '}{formatDate(slip.createdAt)}
                              </small>
                            </div>
                            <div className="d-grid gap-2 mt-1" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                              <div className="bgg-1 bdr-5 bdc-1 p-1 text-center">
                                <div className="bgc-4 px-1 py-1 t-regular bdr-5">
                                  <small>เงินเดิมพันทั้งหมด</small>
                                </div>
                                <div className="px-1 t-regular c-white">
                                  <h4>{formatCurrency(slip.totalAmount ?? 0)}</h4>
                                </div>
                              </div>
                              <div className="bgg-1 bdr-5 bdc-1 p-1 text-center">
                                <div className="bgc-4 px-1 py-1 t-regular bdr-5">
                                  <small>สรุปผลแพ้/ชนะ</small>
                                </div>
                                <div className="px-1 t-regular c-white">
                                  <h4>
                                    {winLoss != null ? formatCurrency(winLoss) : slip.isSettled
                                      ? formatCurrency((slip.totalWinAmount ?? 0) - (slip.totalEffectiveAmount ?? 0))
                                      : '-'}
                                  </h4>
                                </div>
                              </div>
                            </div>
                            <div className="bgg-1 bdr-5 bdc-1 p-1 text-center mt-2">
                              <div className="bgc-4 px-1 py-1 t-regular bdr-5">
                                <small>{refundDisplay.title}</small>
                              </div>
                              <div className="px-1 t-regular c-white">
                                <h4>{formatCurrency(refundDisplay.amount)}</h4>
                                <small className="c-white" style={{ opacity: 0.75 }}>{refundDisplay.subtitle}</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bdr-5 bgc-4 p-2">
                      <h6 className="t-medium c-red mb-2">รายการแทง</h6>
                      <div className="row gap-2 mx--1">
                        {slip.betItems && slip.betItems.length > 0 ? (
                          slip.betItems.map((item, idx) => (
                            <div key={idx} className="col-12 px-1">
                              <div className="bdr-5 bdc-2 bgc-4 p-2">
                                <div className="wrap justify-content-between">
                                  <h6 className="c-red t-medium">ลำดับที่ {idx + 1}</h6>
                                  <div className="wrap gap-1 align-items-center">
                                    {canCustomerCancelItem(slip, item, round) && (
                                      <button
                                        type="button"
                                        className="border-0 bg-warning px-2 py-1 bdr-5 small"
                                        onClick={() => void handleCancelItem(item, idx)}
                                      >
                                        คลิกเพื่อคืนเลข
                                      </button>
                                    )}
                                    {item.status && (
                                      <h6 className={`${getItemStatusClass(item)} c-white px-1 bdr-5`}>
                                        {getItemStatusLabel(item)}
                                      </h6>
                                    )}
                                  </div>
                                </div>
                                <div className="d-grid gap-2 bgc-14 bdr-5 p-2 align-items-center mt-1" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                                  <div className="text-center c-white">
                                    <h5 className="t-medium">{item.number}</h5>
                                    <h6 className="bgc-4 c-red bdr-5 px-1 mx-auto" style={{ width: 'fit-content' }}>
                                      {item.prizeTypeName}
                                    </h6>
                                  </div>
                                  <div className="row mx--1" style={{ rowGap: '0.5rem' }}>
                                    <div className="col-6 px-1">
                                      <div className="bdr-5 bgc-4 c-dark d-block text-center bdc-3">
                                        <small>ยอดแทง</small>
                                        <h6 className="t-medium">{formatCurrency(item.amount)}</h6>
                                      </div>
                                    </div>
                                    <div className="col-6 px-1">
                                      <div className="bdr-5 bgc-4 c-dark d-block text-center bdc-3">
                                        <small>จ่าย x{item.lockedPayoutRate}</small>
                                        <h6 className="t-medium c-green">{item.isWinner && item.winAmount != null ? formatCurrency(item.winAmount) : '-'}</h6>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="col-12 px-1">
                            <p className="c-dark text-center">ไม่มีรายการแทง</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TicketDetailPage
